import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Save, Trash2, Ban, Shield, User, CornerUpLeft, Loader2 } from 'lucide-react';
import PrimaryButton from '../ui/PrimaryButton';
import ConfirmDialog from '../ui/ConfirmDialog';
import CustomSelect from '../ui/CustomSelect';
import CountryPhoneInput from '../ui/CountryPhoneInput';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from '../../contexts/UsersContext';

interface StaffDetailsTabProps {
    data: any;
    onUpdate: (data: any) => void;
    onSave: (data: any) => Promise<any>;
    readOnly?: boolean;
}

const StaffDetailsTab: React.FC<StaffDetailsTabProps> = ({ data, onUpdate, onSave, readOnly = false }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { deleteUser } = useUsers();
    const [isSaving, setIsSaving] = useState(false);

    // Configurazione del dialog di conferma
    const [confirmConfig, setConfirmConfig] = useState<{
        isOpen: boolean;
        title: string;
        description: string;
        confirmLabel: string;
        variant: 'danger' | 'default';
        onConfirm: () => Promise<void> | void;
    }>({
        isOpen: false,
        title: '',
        description: '',
        confirmLabel: '',
        variant: 'default',
        onConfirm: async () => { },
    });

    const closeConfirm = () => setConfirmConfig(prev => ({ ...prev, isOpen: false }));

    const [localData, setLocalData] = useState(() => ({
        ...data,
        first_name: data.first_name || data.firstName || '',
        last_name: data.last_name || data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        role: (data.role).toLowerCase()
    }));

    useEffect(() => {
        setLocalData({
            ...data,
            first_name: data.first_name || data.firstName || '',
            last_name: data.last_name || data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
            role: (data.role).toLowerCase()
        });
    }, [data]);

    const updateField = (field: string, value: any) => {
        setLocalData((prev: any) => ({ ...prev, [field]: value }));
    };

    const handleSaveClick = async () => {
        setIsSaving(true);
        try {
            const res = await onSave(localData);
            if (res.success) {
                toast.success(t('common.save_success'));
                onUpdate(localData);
            } else {
                toast.error(t('common.save_error'));
            }
        } catch (e) {
            toast.error(t('common.unexpected_error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        // Prevent self-deletion
        if (user?.id === localData.id) {
            toast.error(t('staff.details.toast.self_deletion_error'));
            return;
        }

        // Prevent admin deletion
        if (localData.role === 'admin') {
            toast.error(t('staff.details.toast.admin_deletion_error'));
            return;
        }

        setConfirmConfig({
            isOpen: true,
            title: t('common.delete_confirm_title', "Elimina Dipendente"),
            description: t('staff.details.confirm_delete', "Sei sicuro di voler eliminare questo dipendente? Questa azione è irreversibile."),
            confirmLabel: t('common.delete'),
            variant: 'danger',
            onConfirm: async () => {
                const res = await deleteUser(localData.id);
                if (res.success) {
                    toast.success(t('common.delete_success'));
                } else {
                    toast.error(res.error || t('common.delete_error'));
                }
                closeConfirm();
            }
        });
    };

    // Modificato stile input per usare disabled
    const inputStyle = `w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500`;

    return (
        <motion.div
            key="details"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="space-y-6 pt-4 pb-4 px-4 md:px-6"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.details.form.first_name')}</label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            value={localData.first_name}
                            onChange={(e) => updateField('first_name', e.target.value)}
                            className={`${inputStyle} pl-10`}
                            disabled={readOnly}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.details.form.last_name')}</label>
                    <input
                        type="text"
                        value={localData.last_name}
                        onChange={(e) => updateField('last_name', e.target.value)}
                        className={inputStyle}
                        disabled={readOnly}
                    />
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.details.form.email')}</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="email"
                            value={localData.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            className={`${inputStyle} pl-10`}
                            disabled={true}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.details.form.phone')}</label>
                    <div className="relative">
                        <CountryPhoneInput
                            value={localData.phone}
                            onChange={(val) => updateField('phone', val)}
                            disabled={readOnly}
                            placeholder="+39 333 123 4567"
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Role</label>
                    <CustomSelect
                        value={localData.role || 'user'}
                        onChange={(value) => updateField('role', value)}
                        options={[
                            { value: 'user', label: 'Staff' },
                            { value: 'admin', label: 'Admin' }
                        ]}
                        className="w-full text-base"
                        disabled={readOnly}
                    />
                </div>

                {/* Livello permessi (solo per lo staff non-admin; l'admin è il titolare) */}
                {localData.role !== 'admin' && (
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Shield size={14} /> Livello permessi
                        </label>
                        <CustomSelect
                            value={localData.permission_level === 'senior' ? 'senior' : 'junior'}
                            onChange={(value) => updateField('permission_level', value)}
                            options={[
                                { value: 'junior', label: 'Junior — solo i propri appuntamenti' },
                                { value: 'senior', label: 'Senior — agenda completa, senza dati finanziari' }
                            ]}
                            className="w-full text-base"
                            disabled={readOnly}
                        />
                    </div>
                )}
            </div>

            {!readOnly && (
                <div className="pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                    <div className="flex gap-3 w-full sm:w-auto">
                        <button onClick={handleDelete} className="flex-1 sm:flex-none flex items-center justify-center gap-2 text-red-500 hover:bg-red-50 px-3 py-2.5 rounded-xl transition-all text-sm font-bold bg-red-50/50 sm:bg-transparent border border-red-100 sm:border-transparent">
                            <Trash2 size={16} /> {t('common.delete')}
                        </button>
                    </div>
                    <PrimaryButton
                        label={isSaving ? t('common.saving') : t('common.save_changes')}
                        icon={isSaving ? Loader2 : Save}
                        className="w-full sm:w-auto justify-center"
                        onClick={handleSaveClick}
                        disabled={isSaving}
                    />
                </div>
            )}
            {/* Modal di Conferma */}
            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                title={confirmConfig.title}
                description={confirmConfig.description}
                confirmLabel={confirmConfig.confirmLabel}
                variant={confirmConfig.variant}
                onConfirm={confirmConfig.onConfirm}
                onCancel={closeConfirm}
            />
        </motion.div>
    );
};

export default StaffDetailsTab;